/**
 * Where We Are — Home Page
 *
 * Every pickup location as a tile that opens the catalogue for that place.
 * Locations with a photo show it (white type over a teal gradient); the rest
 * get a quiet typographic tile (city name in display type, teal on white)
 * rather than a placeholder image.
 */

import { Link } from 'react-router';
import { ArrowRight, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocations } from '@/hooks/useLocations';
import { useLocale } from '@/i18n/useLocale';
import { buildPublishedLocaleUrl } from '@/i18n/routing';

const listCountries = (countries: string[]): string => {
    if (countries.length <= 1) return countries[0] ?? '';
    return `${countries.slice(0, -1).join(', ')} and ${countries[countries.length - 1]}`;
};

export const WhereWeAreSection = () => {
    const { data: locations, isLoading, isError } = useLocations();
    const locale = useLocale()?.locale ?? 'en';

    if (isLoading || isError || !locations || locations.length === 0) {
        return null;
    }

    const countries = Array.from(
        new Set(locations.map((l) => l.country?.trim()).filter((c): c is string => !!c)),
    );
    const intro = countries.length > 0
        ? `${locations.length} pickup ${locations.length === 1 ? 'point' : 'points'} across ${listCountries(countries)}. Choose one to see what's in stock there.`
        : 'Choose a pickup point to see what\'s in stock there.';

    return (
        <section aria-labelledby="where-we-are-heading" className="bg-background py-14 sm:py-20">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
                    <div>
                        <h2
                            id="where-we-are-heading"
                            className="font-display text-4xl leading-[0.96] tracking-tight text-foreground sm:text-5xl"
                        >
                            Where you can collect
                        </h2>
                        <p className="mt-3 max-w-xl text-base leading-7 text-muted-foreground">{intro}</p>
                    </div>
                    <Link
                        to={buildPublishedLocaleUrl(locale, '/locations')}
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full text-sm font-semibold text-primary underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                        Opening hours and pickup details
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                </div>

                <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))]" aria-label="Pickup locations">
                    {locations.map((location) => {
                        const imageUrl = location.imageUrl?.trim();
                        const city = location.city?.trim() || location.name;
                        const country = location.country?.trim();
                        const href = buildPublishedLocaleUrl(locale, `/catalogue/${location.slug || location.id}`);

                        return (
                            <li key={location.id}>
                                <Link
                                    to={href}
                                    className={cn(
                                        'group relative flex min-h-[6.5rem] flex-col justify-end overflow-hidden rounded-2xl border shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                        imageUrl
                                            ? 'border-border/80 bg-primary text-white hover:shadow-[0_20px_44px_rgba(15,118,110,0.2)]'
                                            : 'border-border/80 bg-white text-primary hover:border-primary/30 hover:shadow-[0_20px_44px_rgba(15,118,110,0.12)]',
                                    )}
                                >
                                    {imageUrl && (
                                        <>
                                            <img
                                                src={imageUrl}
                                                alt={location.name}
                                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,60,58,0.92)] via-[rgba(10,60,58,0.35)] to-transparent" aria-hidden="true" />
                                        </>
                                    )}
                                    {!imageUrl && (
                                        <MapPin
                                            className="absolute right-3 top-3 h-4 w-4 text-primary/40 transition-colors group-hover:text-primary"
                                            aria-hidden="true"
                                        />
                                    )}
                                    <div className="relative p-4">
                                        <p className="font-display text-xl leading-tight">{city}</p>
                                        {country && (
                                            <p className={cn(
                                                'mt-1 text-[11px] font-semibold uppercase tracking-[0.16em]',
                                                imageUrl ? 'text-white/75' : 'text-muted-foreground',
                                            )}>
                                                {country}
                                            </p>
                                        )}
                                    </div>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
};

export default WhereWeAreSection;
